import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Usb, Wifi, Signal, SignalHigh, SignalMedium, SignalLow, Check } from "lucide-react";
import { ToolIcon } from "@/components/tool-icon";
import { useSelectedTool } from "@/lib/tool-context";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const SPARROW_DEVICES = [
  { id: "Sparrow 312", signal: "strong" as const },
  { id: "Sparrow 415", signal: "medium" as const },
  { id: "Sparrow 198", signal: "weak" as const },
];

function SignalIcon({ strength, className }: { strength: "strong" | "medium" | "weak"; className?: string }) {
  if (strength === "strong") return <SignalHigh className={className} />;
  if (strength === "medium") return <SignalMedium className={className} />;
  return <SignalLow className={className} />;
}

function signalColor(strength: "strong" | "medium" | "weak") {
  if (strength === "strong") return "text-green-500";
  if (strength === "medium") return "text-amber-500";
  return "text-red-400";
}

export default function Dashboard() {
  const { setSelectedTool } = useSelectedTool();
  const [, navigate] = useLocation();
  const [showWifiDialog, setShowWifiDialog] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);

  const handleSparrowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowWifiDialog(true);
    setSelectedDevice(null);
  };

  const handleConnect = () => {
    setSelectedTool("sparrow");
    setShowWifiDialog(false);
    navigate("/field");
  };

  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="grid grid-cols-2 gap-8 max-w-2xl w-full">
        <Link href="/field" onClick={() => setSelectedTool("dragonball")}>
          <div
            className="flex flex-col items-center gap-4 p-8 rounded-xl border-2 bg-card cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] border-green-500 shadow-green-500/10 shadow-md"
            data-testid="card-tool-dragonball"
          >
            <ToolIcon type="dragonball" className="h-32 w-32" />
            <span className="text-lg font-semibold">DragonBall</span>
            <span className="text-xs text-muted-foreground flex items-center gap-1"><Usb className="h-3 w-3" />USB</span>
          </div>
        </Link>
        <div
          className="flex flex-col items-center gap-4 p-8 rounded-xl border-2 bg-card cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] border-green-500 shadow-green-500/10 shadow-md"
          onClick={handleSparrowClick}
          data-testid="card-tool-sparrow"
        >
          <ToolIcon type="sparrow" className="h-32 w-32" />
          <span className="text-lg font-semibold">Sparrow</span>
          <span className="text-xs text-muted-foreground flex items-center gap-1"><Wifi className="h-3 w-3" />Wi-Fi</span>
        </div>
      </div>

      <Dialog open={showWifiDialog} onOpenChange={setShowWifiDialog}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-sparrow-wifi">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wifi className="h-5 w-5 text-primary" />
              Available Sparrows
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Select a Sparrow to connect to:
          </p>
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {SPARROW_DEVICES.map((device) => (
              <button
                key={device.id}
                className={`w-full text-left px-3 py-3 rounded-lg border transition-all flex items-center gap-3 ${
                  selectedDevice === device.id
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "hover:bg-muted/50 hover:border-muted-foreground/20"
                }`}
                onClick={() => setSelectedDevice(device.id)}
                data-testid={`wifi-device-${device.id}`}
              >
                <Signal className={`h-5 w-5 shrink-0 ${signalColor(device.signal)}`} />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium" data-testid={`text-device-label-${device.id.toLowerCase().replace(/\s+/g, "-")}`}>{device.id}</span>
                </div>
                <span data-testid={`icon-signal-${device.id.toLowerCase().replace(/\s+/g, "-")}`}>
                  <SignalIcon strength={device.signal} className={`h-4 w-4 ${signalColor(device.signal)}`} />
                </span>
                {selectedDevice === device.id && (
                  <Check className="h-4 w-4 text-primary shrink-0" />
                )}
              </button>
            ))}
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowWifiDialog(false)} data-testid="button-cancel-wifi">
              Cancel
            </Button>
            <Button disabled={!selectedDevice} onClick={handleConnect} data-testid="button-connect-wifi">
              Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
